import { createFileRoute, type ToOptions } from '@tanstack/react-router'
import { Calendar, CalendarCheck2, LayoutGrid, Users, type LucideIcon } from 'lucide-react'
import z from 'zod/v3'

import { typedObjectEntries } from '@gozoki/tools'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { StoreCombobox } from '~/components/new/app-ui/store-combobox'
import { Title } from '~/components/new/app-ui/title'
import { PlanningAttendance } from '~/components/routes/hr/planning/attendance'
import { PlanningCollaborator } from '~/components/routes/hr/planning/collaborators'
import { periods, PlanningDashboard } from '~/components/routes/hr/planning/dashboard'
import { PlanningCalendar } from '~/components/routes/hr/planning/planning'

const tabValues = ['dashboard', 'calendar', 'collaborators', 'attendance'] as const

export const Route = createFileRoute('/_authenticated/hr/planning/$tab')({
  component: RouteComponent,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
  validateSearch: z.object({
    // Global search params
    storeId: z.number().optional(),

    // Dashboard specific search params
    period: z.enum(periods).optional(),
  }),
})

const tabOptions = {
  dashboard: {
    label: 'Tableau de bord',
    icon: LayoutGrid,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'dashboard' } },
    component: <PlanningDashboard />,
  },
  calendar: {
    label: 'Planning',
    icon: Calendar,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'calendar' } },
    component: <PlanningCalendar />,
  },
  collaborators: {
    label: 'Personnel',
    icon: Users,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'collaborators' } },
    component: <PlanningCollaborator />,
  },
  attendance: {
    label: 'Pointage',
    icon: CalendarCheck2,
    toOptions: { to: '/hr/planning/$tab', params: { tab: 'attendance' } },
    component: <PlanningAttendance />,
  },
} as const satisfies Record<
  (typeof tabValues)[number],
  { toOptions: ToOptions; component: React.ReactNode; label: string; icon: LucideIcon }
>

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useSearch()
  const { tab } = Route.useParams()

  const CurrentTabComponent = tabOptions[tab].component

  return (
    <>
      <header className="flex items-center justify-between mb-4">
        <Title>RH & Planning</Title>

        <StoreCombobox
          onChange={(v) => navigate({ search: (prev) => ({ ...prev, storeId: v ?? undefined }) })}
          defaultValue={storeId ?? null}
        />
      </header>

      <AppTabs
        className="mb-4"
        tabs={typedObjectEntries(tabOptions).map(([key, value]) => ({
          label: value.label,
          icon: value.icon,
          value: key,
        }))}
        value={tab}
        onTabChange={(v) =>
          navigate({ ...tabOptions[v].toOptions, search: (prev) => ({ storeId: prev.storeId }) })
        }
      />

      {CurrentTabComponent}
    </>
  )
}
